






























































import {Component, Vue} from 'vue-property-decorator';
import {Action, Getter} from 'vuex-class';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import LoadingSpinner from '@/shared/components/loadingSpinner/LoadingSpinner.vue';
import ConfirmationModal from '@/shared/components/ConfirmationModal.vue';
import Toast from '@/shared/services/toast';
import {HANDLE_SERIOUS_ERROR} from '@/store/app/actionTypes';
import {emailApi} from '../services/emailApiService';
import * as ace from 'brace';
import 'brace/mode/html';
import 'brace/theme/monokai';
import 'brace/ext/language_tools';
import EditableText from '@/shared/components/EditableText.vue';
import {sanitizeHtml} from '@/shared/util/sanitize-html/sanitizeHtml';
import {Issues} from '@/shared/util/sanitize-html/types';
import {emailTokenPlaceholders} from '../services/emailTokenPlaceholders';
import {htmlFormatter} from '../services/htmlFormatter';
import { SANITIZE_OPTIONS } from '@/shared/util/sanitize-html/EmailTemplateSanitizeOptions';

const TOKENS = {
	messages: '{{ messages }}',
	senderName: '{{ senderName }}'
};

@Component({
	components: {
		ConfirmationModal,
		LoadingSpinner,
		EditableText
	}
})
export default class Flow extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Action(HANDLE_SERIOUS_ERROR) public handleSeriousError: any;

	private templateId: string = '';
	private templateName: string = '';
	private templateHtml: string = '';
	private previewHtml: string = '';
	private templateHtmlHasIssues: boolean = false;
	private templateChanged: boolean = false;

	private readonly EDITOR_ID = 'email-template-editor';
	private editor: ace.Editor;
	private editorAnnotationTimeout: number;

	private templateLoaded: boolean = false;
	private updateInProgress: boolean = false;
	private showDiscardChangesModel: boolean = false;

	public async mounted() {
		this.templateId = this.$route.params.templateId;
		this.templateName = this.$route.params.templateName;
		await this.loadTemplate();
		this.setupAceEditor();
	}

	public destroyed() {
		this.templateHtml = '';
		this.previewHtml = '';
		this.templateChanged = false;
	}

	private async loadTemplate() {
		if (this.templateId) {
			try {
				const htmlContent: string = await emailApi.getTemplateContent(this.currentWorkspace.id, this.templateId);
				this.templateHtml = htmlContent;
				this.setAceEditorValue(htmlContent);
				this.setPreviewValue(htmlContent);
				this.templateLoaded = true;
			} catch (error) {
				if (error.status !== 404) {
					this.handleSeriousError(error.message);
				}
			}
		} else {
			this.templateHtml = `<!DOCTYPE html>\r\n<html><head></head><body>${TOKENS.messages}</body></html>`;
			this.setAceEditorValue(TOKENS.messages);
			this.setPreviewValue(this.templateHtml);
			this.templateLoaded = true;
		}
	}

	private setupAceEditor() {
		if (this.isEditorReadyForSetup()) {
			const langTools = ace.acequire('ace/ext/language_tools');
			langTools.setCompleters([]);

			this.editor = ace.edit(this.EDITOR_ID);
			this.editor.getSession().setMode('ace/mode/html');
			this.editor.setTheme('ace/theme/monokai');
			this.editor.setShowPrintMargin(false);
			this.editor.session.on('change', (event: ace.EditorChangeEvent) => this.setTemplateHtmlOnChange());

			this.editor.setOptions({
				enableBasicAutocompletion: true
			});

			const customCompleter = {
				getCompletions: (editor: ace.Editor, session: ace.IEditSession, pos: number, prefix: any, callback: any) => {
					const suggestions = [
						{caption: 'Content Placeholder', value: TOKENS.messages, meta: 'Metadata'},
						{caption: 'Sender\'s Name Placeholder', value: TOKENS.senderName, meta: 'Metadata'}
					];

					callback(null, suggestions);
				}
			};

			langTools.addCompleter(customCompleter);

			if (this.templateHtml) {
				this.setAceEditorValue(this.templateHtml);
			}

			this.editor.resize();
		}
	}

	private isEditorReadyForSetup(): boolean {
		return !this.editor && !!document.getElementById(this.EDITOR_ID);
	}

	private setTemplateHtmlOnChange() {
		const newContent: string = this.editor.session.getValue();

		if (this.templateHtml !== newContent) {
			this.updateTemplateHtml(newContent);
		}
	}

	private updateTemplateHtml(newContent: string) {
		this.templateHtml = newContent;

		this.setPreviewValue(this.getSafeHtml(newContent));
		this.templateChanged = true;
	}

	private setPreviewValue(html: string) {
		const whiteBackgroundStyle = '<style>body {background-color: white;}</style>';
		this.previewHtml = whiteBackgroundStyle + this.replaceTokens(html);
	}

	private setAceEditorValue(newValue: string) {
		if (this.editor) {
			this.editor.session.setValue(newValue);
		}
	}

	private replaceTokens(newContent: string): string {
		return newContent
			.replace(TOKENS.messages, emailTokenPlaceholders.getContentPlaceholder())
			.replace(TOKENS.senderName, emailTokenPlaceholders.getSenderNamePlaceholder());
	}

	private getSafeHtml(unCleaned: string): string {
		// https://www.npmjs.com/package/sanitize-html

		const response = sanitizeHtml(unCleaned, SANITIZE_OPTIONS);
		this.showEditorWarningsAfterDelay(response.issues);
		return response.output;
	}

	private showEditorWarningsAfterDelay(issues: Issues[]) {
		this.templateHtmlHasIssues = issues.length > 0;
		if (this.editorAnnotationTimeout) {
			clearTimeout(this.editorAnnotationTimeout);
		}
		this.editorAnnotationTimeout = window.setTimeout(() => this.showEditorWarnings(issues), 1000);
	}

	private showEditorWarnings(issues: Issues[]) {
		this.editor.getSession().setAnnotations(issues.map(issue => {
			return {
				row: issue.line - 1,
				column: issue.column,
				text: issue.description,
				type: 'error' // also warning and information
			};
		}));
	}

	private onNameChange(newName: string) {
		this.templateChanged = true;
		this.templateName = newName;
	}

	private async saveTemplate() {
		if (this.validateSaveRequest()) {
			try {
				this.updateInProgress = true;
				this.templateHtml = htmlFormatter.format(this.templateHtml);
				await this.callSaveApi();
				this.templateChanged = false;
				Toast.showSuccess('Template saved');
				this.goBackToEmailSettings();
			} catch (err) {
				Toast.showError(err.message);
			} finally {
				this.updateInProgress = false;
			}
		}
	}

	private callSaveApi(): Promise<void> {
		if (this.templateId) {
			return emailApi.saveEmailTemplate(this.currentWorkspace.id, this.templateId, this.templateName, this.templateHtml);
		} else {
			return emailApi.createEmailTemplate(this.currentWorkspace.id, this.templateName, this.templateHtml);
		}
	}

	private validateSaveRequest(): boolean {
		if (!this.templateName) {
			Toast.showError('Template name cannot be empty');
			return false;
		}
		if (this.templateHtml.indexOf(TOKENS.messages) === -1) {
			Toast.showError(`Placeholder ${TOKENS.messages} must be present in template`);
			return false;
		}
		if (this.templateHtmlHasIssues) {
			Toast.showError(`HTML content contains issues, please resolve before saving`);
			return false;
		}
		return true;
	}

	private cancel() {
		if (this.templateChanged) {
			this.showDiscardChangesModel = true;
		} else {
			this.goBackToEmailSettings();
		}
	}

	private goBackToEmailSettings() {
		this.$router.push({name: 'email'});
	}
}
